import type { ScheduleConfig, DayOfWeek } from '@cmms/types'

/**
 * Human-readable summary of a schedule_config — e.g.
 *   { type: 'daily', time: '09:00' } → "Daily at 09:00"
 *   { type: 'weekly', days: ['mon','wed'], time: '08:30' } → "Weekly Mon/Wed at 08:30"
 *   { type: 'monthly', day_of_month: 15, time: '09:00' } → "Monthly day 15 at 09:00"
 * Returns null for malformed configs.
 */
const DAY_SHORT: Record<DayOfWeek, string> = {
  sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat',
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/// Canonical times-of-day list. Newer schedules carry `times: [...]`;
/// older rows just have `time: '09:00'`. Either way we end up with a
/// sorted unique list of HH:MM strings the caller can render.
function effectiveTimes(cfg: ScheduleConfig | null | undefined): string[] {
  if (!cfg) return []
  const list = Array.isArray(cfg.times) && cfg.times.length > 0
    ? cfg.times
    : (cfg.time ? [cfg.time] : [])
  return [...new Set(list.filter((t): t is string =>
    typeof t === 'string' && /^\d{2}:\d{2}$/.test(t)))]
    .sort()
}

export function summarizeSchedule(cfg: ScheduleConfig | null | undefined): string | null {
  if (!cfg || !cfg.type) return null
  // Format the time portion. Single time reads as " at 09:00"; multi
  // reads as " at 09:00, 13:00, 17:00" so the user instantly sees how
  // many spawns to expect per day.
  const times = effectiveTimes(cfg)
  const at = times.length === 0 ? ''
           : times.length === 1 ? ` at ${times[0]}`
           : ` at ${times.join(', ')}`
  // Recurrence cap, if any.
  const until = cfg.end_date ? ` until ${cfg.end_date}` : ''
  let head: string
  switch (cfg.type) {
    case 'daily':
      head = 'Daily'; break
    case 'weekly': {
      const days = (cfg.days ?? []).map((d) => DAY_SHORT[d]).filter(Boolean)
      head = days.length ? `Weekly ${days.join('/')}` : 'Weekly'
      break
    }
    case 'monthly':
    case 'quarterly': {
      const label = cfg.type === 'monthly' ? 'Monthly' : 'Quarterly'
      // Prefer the new multi-day array; fall back to legacy single.
      const doms = Array.isArray((cfg as any).days_of_month)
        ? ((cfg as any).days_of_month as number[])
        : (cfg.day_of_month ? [cfg.day_of_month] : [])
      if (doms.length === 0) head = label
      else if (doms.length === 1) head = `${label} day ${doms[0]}`
      else head = `${label} (${doms.length}× — days ${doms.join(', ')})`
      break
    }
    case 'yearly': {
      const dates = Array.isArray((cfg as any).dates) ? ((cfg as any).dates as string[]) : null
      if (dates && dates.length > 0) {
        if (dates.length === 1) {
          const parts = dates[0]!.split('-').map(Number)
          const mo = parts[0] ?? 0
          const dy = parts[1] ?? 0
          const m = mo >= 1 && mo <= 12 ? MONTHS[mo - 1] : null
          head = m ? `Yearly ${m} ${dy}` : 'Yearly'
        } else {
          head = `Yearly (${dates.length}× per year)`
        }
      } else {
        const m = cfg.month && cfg.month >= 1 && cfg.month <= 12 ? MONTHS[cfg.month - 1] : null
        if (m && cfg.day_of_month) head = `Yearly ${m} ${cfg.day_of_month}`
        else if (m) head = `Yearly in ${m}`
        else head = 'Yearly'
      }
      break
    }
    default:
      return null
  }
  return `${head}${at}${until}`
}

/**
 * Best-effort "next fire moment" computed client-side from a schedule_config.
 * The server's `mostRecentFireMoment` is the source of truth — this is only
 * for showing a heads-up like "Next: Tomorrow 9am" on the template card.
 *
 * Uses the BROWSER tz, not the org tz. For most cases the difference is
 * cosmetic; precise scheduling is enforced server-side regardless.
 *
 * Returns null if the config is missing/unparseable.
 */
export function nextFireMoment(cfg: ScheduleConfig | null | undefined, from: Date = new Date()): Date | null {
  if (!cfg) return null
  const times = effectiveTimes(cfg)
  if (times.length === 0) return null

  // Optional cap — drop any candidate strictly after end_date 23:59.
  let endCap: Date | null = null
  if (cfg.end_date) {
    const em = /^(\d{4})-(\d{2})-(\d{2})$/.exec(cfg.end_date)
    if (em) {
      endCap = new Date(
        parseInt(em[1]!, 10),
        parseInt(em[2]!, 10) - 1,
        parseInt(em[3]!, 10),
        23, 59, 59, 999,
      )
    }
  }

  const parseHHMM = (s: string): [number, number] => {
    const m = /^(\d{2}):(\d{2})$/.exec(s)!
    return [parseInt(m[1]!, 10), parseInt(m[2]!, 10)]
  }

  // For a given calendar day, return the earliest configured time-of-day
  // that's still in the future and within the end-date cap.
  const firstFutureOnDay = (base: Date): Date | null => {
    for (const t of times) {
      const [hh, mm] = parseHHMM(t)
      const c = new Date(base)
      c.setHours(hh, mm, 0, 0)
      if (c > from && (!endCap || c <= endCap)) return c
    }
    return null
  }

  switch (cfg.type) {
    case 'daily': {
      // Today first; if all of today's slots have passed, walk forward
      // one day at a time until we either find a match or trip the cap.
      for (let i = 0; i < 7; i++) {
        const day = new Date(from); day.setDate(day.getDate() + i)
        const fire = firstFutureOnDay(day)
        if (fire) return fire
        if (endCap && day > endCap) return null
      }
      return null
    }
    case 'weekly': {
      const want = new Set<number>(
        (cfg.days ?? []).map((d) => ({ sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 } as const)[d as DayOfWeek]),
      )
      if (want.size === 0) return null
      for (let i = 0; i < 14; i++) {
        const day = new Date(from); day.setDate(day.getDate() + i)
        if (!want.has(day.getDay())) continue
        const fire = firstFutureOnDay(day)
        if (fire) return fire
        if (endCap && day > endCap) return null
      }
      return null
    }
    case 'monthly':
    case 'quarterly': {
      const doms = Array.isArray((cfg as any).days_of_month)
        ? ((cfg as any).days_of_month as number[])
            .filter((n) => Number.isInteger(n) && n >= 1 && n <= 31)
        : (cfg.day_of_month ? [cfg.day_of_month] : [1])
      if (doms.length === 0) return null
      const step = cfg.type === 'quarterly' ? 3 : 1
      // Walk forward month-by-month (or quarter-by-quarter), trying
      // each candidate day in order. First slot strictly after `from`
      // wins. 24 periods caps the search at ~2 years monthly / ~6 years
      // quarterly — plenty for a "next fire" preview.
      for (let i = 0; i < 24; i++) {
        const baseMonth = new Date(from.getFullYear(), from.getMonth() + i * step, 1)
        const lastDay = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 1, 0).getDate()
        const sortedDoms = [...doms].sort((a, b) => a - b)
        for (const dom of sortedDoms) {
          const candidate = new Date(baseMonth)
          candidate.setDate(Math.min(dom, lastDay))
          if (endCap && candidate > endCap) return null
          const fire = firstFutureOnDay(candidate)
          if (fire) return fire
        }
      }
      return null
    }
    case 'yearly': {
      // Prefer new `dates: ["MM-DD"]` array; fall back to legacy
      // single `{month, day_of_month}` pair.
      let pairs: Array<{ month0: number; day: number }>
      const datesRaw = Array.isArray((cfg as any).dates) ? ((cfg as any).dates as string[]) : null
      if (datesRaw && datesRaw.length > 0) {
        pairs = datesRaw
          .map((s) => s.split('-').map(Number))
          .filter((parts): parts is [number, number] =>
            parts.length === 2 && typeof parts[0] === 'number' && typeof parts[1] === 'number'
            && parts[0] >= 1 && parts[0] <= 12 && parts[1] >= 1 && parts[1] <= 31)
          .map(([mo, dy]) => ({ month0: mo - 1, day: dy }))
      } else {
        const month = (cfg.month && cfg.month >= 1 && cfg.month <= 12) ? cfg.month - 1 : 0
        pairs = [{ month0: month, day: Math.max(1, Math.min(31, cfg.day_of_month ?? 1)) }]
      }
      if (pairs.length === 0) return null
      const sortedPairs = [...pairs].sort((a, b) => a.month0 - b.month0 || a.day - b.day)
      for (let i = 0; i < 5; i++) {
        for (const { month0, day } of sortedPairs) {
          const candidate = new Date(from.getFullYear() + i, month0, 1)
          const lastDay = new Date(candidate.getFullYear(), candidate.getMonth() + 1, 0).getDate()
          candidate.setDate(Math.min(day, lastDay))
          if (endCap && candidate > endCap) return null
          const fire = firstFutureOnDay(candidate)
          if (fire) return fire
        }
      }
      return null
    }
    default:
      return null
  }
}

/**
 * "Tomorrow 9am" / "in 3 days" style relative label for a future date.
 * Returns absolute date for instants more than a week away.
 */
export function formatNextFire(when: Date, from: Date = new Date()): string {
  const diff = when.getTime() - from.getTime()
  if (diff <= 0) return 'now'
  const hh = String(when.getHours()).padStart(2, '0')
  const mm = String(when.getMinutes()).padStart(2, '0')
  const timeStr = `${hh}:${mm}`

  const startOfToday = new Date(from); startOfToday.setHours(0, 0, 0, 0)
  const days = Math.floor((new Date(when).setHours(0, 0, 0, 0) - startOfToday.getTime()) / 86_400_000)
  if (days === 0) return `Today ${timeStr}`
  if (days === 1) return `Tomorrow ${timeStr}`
  if (days < 7) {
    const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][when.getDay()]
    return `${wd} ${timeStr}`
  }
  // Beyond a week — show a concise absolute date
  const mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][when.getMonth()]
  return `${mo} ${when.getDate()} ${timeStr}`
}
